/**
 * Conditions editor — Voluum-parity inline editor for `conditionalPathsGroups[*].conditions`.
 *
 * Local Cancel/Apply: edits a `useConditionsDraft` snapshot and `Apply` calls
 * `onApply` with the rebuilt `VoluumPathGroupConditions` (which preserves any
 * unknown keys the registry doesn't model).
 */

import type { FC } from 'react';
import type {
  VoluumPathGroupConditions,
  VoluumPathGroupUpdate,
} from '@trafficpulse/shared';

import { ConditionRow } from './condition-row';
import {
  ConditionsEditorFooter,
  ConditionsEditorHeader,
} from './ConditionsEditorActions';
import ConditionsEditorAddRow from './ConditionsEditorAddRow';
import { useConditionsDraft } from './use-conditions-draft';
import { getPathGroupDisplayLabel } from '../path-group-display-label';

export interface ConditionsEditorProps {
  group: VoluumPathGroupUpdate;
  onApply: (next: VoluumPathGroupConditions) => void;
  onClose: () => void;
}

const ConditionsEditor: FC<ConditionsEditorProps> = ({
  group,
  onApply,
  onClose,
}) => {
  const draft = useConditionsDraft(group.conditions);

  const handleApply = () => {
    onApply(draft.buildConditions());
  };

  const handleCancel = () => {
    if (draft.isDirty) draft.reset();
    else onClose();
  };

  const groupName = getPathGroupDisplayLabel(group);
  const takenKeys = draft.rows.map((r) => r.descriptor.key);

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <ConditionsEditorHeader
        groupName={groupName}
        rowCount={draft.rows.length}
        onDeleteAll={draft.clearAll}
      />
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-0.5">
        {draft.rows.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/60 bg-muted/10 p-3 text-[11px] leading-snug text-muted-foreground">
            No conditions yet. Add one below — leaving the list empty matches
            all traffic for this group.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {draft.rows.map((row, idx) => (
              <ConditionRow
                key={row.descriptor.key}
                row={row}
                onPredicateChange={(p) => draft.setPredicateAt(idx, p)}
                onValuesChange={(v) => draft.setValuesAt(idx, v)}
                onTimeZoneChange={(tz) => draft.setTimeZoneAt(idx, tz)}
                onRemove={() => draft.removeAt(idx)}
              />
            ))}
          </ul>
        )}
        <ConditionsEditorAddRow
          takenKeys={takenKeys}
          onAdd={draft.addCondition}
        />
      </div>
      <ConditionsEditorFooter
        isDirty={draft.isDirty}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </div>
  );
};

export default ConditionsEditor;
